import { Alert, Stack } from '@mui/material'
import React from 'react'

import { isBlazeMeterExternalActionRequest } from 'components/scenarios/utils/graph/nodes/typeguards'
import {
  ExternalActionRequestType,
  type RollbackExternalActionNode
} from 'types/api/scenarios/scenario'
import {
  getAWSAutoscalingGroupNameFromASGArn,
  getAWSLoadBalancerNameFromLoadBalancerArn
} from 'utils/status-checks/status-check-integrations-util-aws'

const ERROR_ALERT_TARGET = {
  [ExternalActionRequestType.RemoveAWSSecurityGroups]: 'AWS Security Group',
  [ExternalActionRequestType.DisableAWSAutoScalingGroups]:
    'AWS Auto Scaling Group'
}
const ERROR_ALERT_SUFFIX = {
  [ExternalActionRequestType.RemoveAWSSecurityGroups]:
    'ASG rules are enabled again',
  [ExternalActionRequestType.DisableAWSAutoScalingGroups]:
    'ASG is set to scale again'
}

function getRollbackFailureMessage(node: RollbackExternalActionNode) {
  const { externalActionRequest } = node
  const { has_state_been_modified: hasStateBeenModified } = node.state

  const errorAlertResult = hasStateBeenModified
    ? 'was unstable'
    : 'was unable to be performed'

  if (isBlazeMeterExternalActionRequest(externalActionRequest)) {
    return `Urgent: The rollback step for your BlazeMeter test ${externalActionRequest.generatorName}, ${errorAlertResult}. Visit BlazeMeter to ensure your test has stopped.`
  }

  const externalActionRequestType = externalActionRequest.type
  const externalActionRequestResourceArn = externalActionRequest.resourceArn

  const errorAlertTarget = ERROR_ALERT_TARGET[externalActionRequestType]
  const errorAlertSuffix = ERROR_ALERT_SUFFIX[externalActionRequestType]

  const resourceName =
    externalActionRequestType ===
    ExternalActionRequestType.RemoveAWSSecurityGroups
      ? getAWSLoadBalancerNameFromLoadBalancerArn(
          externalActionRequestResourceArn
        )
      : getAWSAutoscalingGroupNameFromASGArn(externalActionRequestResourceArn)

  return `Urgent: The rollback step for your ${errorAlertTarget} ${resourceName}, ${errorAlertResult}. Visit your AWS Console to ensure your ${errorAlertSuffix}.`
}

export const ScenarioRollbackFailureAlerts: React.FC<{
  externalRollbacks: RollbackExternalActionNode[]
}> = ({ externalRollbacks }) => {
  return (
    <Stack sx={{ rowGap: 2 }}>
      {externalRollbacks.map((node) => {
        const rollbackFailureMessage = getRollbackFailureMessage(node)

        return (
          <Alert
            key={node.id}
            data-testid={`external-rollback-node-failure-alert-${node.guid}`}
            severity="error"
            variant="filled"
          >
            {rollbackFailureMessage}
          </Alert>
        )
      })}
    </Stack>
  )
}
